import { supabase } from './supabaseClient';
import type { Discount } from '@/types/discount';

interface DiscountRow {
  id: string;
  code: string | null;
  label: string;
  type: string;
  value: number;
  applies_to: string;
  skus: string[];
  min_order_amount: number | null;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  used_count: number;
  status: string;
}

function mapRow(r: DiscountRow): Discount {
  return {
    id: r.id,
    code: r.code,
    label: r.label,
    type: r.type as 'percent' | 'fixed',
    value: Number(r.value),
    appliesTo: r.applies_to as 'all' | 'specific',
    skus: r.skus ?? [],
    minOrderAmount: r.min_order_amount !== null ? Number(r.min_order_amount) : null,
    startsAt: r.starts_at,
    endsAt: r.ends_at,
    maxUses: r.max_uses,
    usedCount: r.used_count,
    status: r.status as 'active' | 'inactive',
  };
}

function isWithinWindow(d: Discount): boolean {
  const now = Date.now();
  if (d.startsAt && new Date(d.startsAt).getTime() > now) return false;
  if (d.endsAt && new Date(d.endsAt).getTime() < now) return false;
  if (d.maxUses !== null && d.usedCount >= d.maxUses) return false;
  return true;
}

export interface CartItemForDiscount {
  sku: string;
  qty: number;
  unitPrice: number;
}

export interface DiscountPreview {
  discount: Discount;
  amount: number;
}

// Product-page display only (never used for cart/checkout math — that stays
// on the plain core price, with the discount applied once, separately, at
// checkout). Returns null if the discount doesn't apply to this SKU.
export function applyDiscountToPrice(originalPrice: number, discount: Discount, sku: string): number | null {
  if (discount.appliesTo === 'specific' && !discount.skus.includes(sku)) return null;
  const raw = discount.type === 'percent' ? originalPrice * (discount.value / 100) : discount.value;
  const capped = Math.min(raw, originalPrice);
  return Math.round(originalPrice - capped);
}

function computeDiscountAmount(discount: Discount, items: CartItemForDiscount[]): number {
  const qualifying = discount.appliesTo === 'specific' ? items.filter((it) => discount.skus.includes(it.sku)) : items;
  const qualifyingSubtotal = qualifying.reduce((s, it) => s + it.unitPrice * it.qty, 0);
  if (qualifyingSubtotal <= 0) return 0;
  const raw = discount.type === 'percent' ? qualifyingSubtotal * (discount.value / 100) : discount.value;
  return Math.round(Math.min(raw, qualifyingSubtotal));
}

export async function fetchAutoDiscount(): Promise<Discount | null> {
  const { data } = await supabase
    .from('rxk_discounts')
    .select('*')
    .eq('status', 'active')
    .is('code', null)
    .order('created_at', { ascending: false })
    .limit(1);
  const row = ((data as DiscountRow[]) ?? [])[0];
  if (!row) return null;
  const discount = mapRow(row);
  return isWithinWindow(discount) ? discount : null;
}

export async function previewAutoDiscount(items: CartItemForDiscount[]): Promise<DiscountPreview | null> {
  const discount = await fetchAutoDiscount();
  if (!discount) return null;
  const subtotal = items.reduce((s, it) => s + it.unitPrice * it.qty, 0);
  if (discount.minOrderAmount !== null && subtotal < discount.minOrderAmount) return null;
  const amount = computeDiscountAmount(discount, items);
  return amount > 0 ? { discount, amount } : null;
}

export async function validateCoupon(
  rawCode: string,
  items: CartItemForDiscount[],
): Promise<{ ok: true; preview: DiscountPreview } | { ok: false; error: string }> {
  const code = rawCode.trim().toUpperCase();
  if (!code) return { ok: false, error: 'Enter a code' };

  const { data } = await supabase.from('rxk_discounts').select('*').eq('status', 'active').eq('code', code).maybeSingle();
  if (!data) return { ok: false, error: 'Invalid or expired code' };

  const discount = mapRow(data as DiscountRow);
  if (!isWithinWindow(discount)) return { ok: false, error: 'Invalid or expired code' };

  const subtotal = items.reduce((s, it) => s + it.unitPrice * it.qty, 0);
  if (discount.minOrderAmount !== null && subtotal < discount.minOrderAmount) {
    return { ok: false, error: `Minimum order ৳${discount.minOrderAmount.toLocaleString()} required` };
  }

  const amount = computeDiscountAmount(discount, items);
  if (amount <= 0) return { ok: false, error: 'This code does not apply to your cart' };

  return { ok: true, preview: { discount, amount } };
}
